import type { FormulaTokenDto } from '../../../api/generated/models';
import { MAX_FORMULA_TOKENS, SLUG_PATTERN } from './calculatedMetrics.constants';
import type { CalculatedMetricRowState } from './campaignForm.types';

const SERVED_METRICS = new Set(['served_impression_count', 'revenue', 'cost', 'cpm', 'rpm']);
const ATTRIBUTION_METRICS = new Set(['matched_impression_count']);
const PIXEL_METRICS = new Set([...SERVED_METRICS, ...ATTRIBUTION_METRICS]);
const ADVERTISER_METRICS = new Set(['advertiser_event_count']);
const EVENT_REF_KINDS = new Set([
  'event',
  'sum_event',
  'matched_event',
  'count_event',
]);
const REF_KINDS = new Set(['metric', ...EVENT_REF_KINDS]);
const ALL_KNOWN_METRICS = new Set([...PIXEL_METRICS, ...ADVERTISER_METRICS]);
const VALID_OPS = new Set(['+', '-', '*', '/']);

function isOperand(t: FormulaTokenDto): boolean {
  return t.t === 'num' || t.t === 'ref';
}

function validateFormulaTokens(
  formula: { tokens: FormulaTokenDto[] },
  validEventKeys: string[],
  targetMode: 'new' | 'override',
  key: string,
  targetColumn?: string,
  knownCalcKeys: string[] = [],

  catalogKeys: Set<string> = new Set()
): string[] {
  const errors: string[] = [];
  const knownCalcKeySet = new Set(knownCalcKeys);

  if (!formula || !Array.isArray(formula.tokens)) {
    return ['formula.tokens must be an array'];
  }

  const tokens = formula.tokens;

  if (tokens.length === 0) {
    errors.push('formula.tokens must not be empty');
    return errors;
  }
  if (tokens.length > MAX_FORMULA_TOKENS) {
    errors.push(`formula.tokens exceeds the maximum of ${MAX_FORMULA_TOKENS} tokens`);
  }

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (!tok || typeof tok.t !== 'string') {
      errors.push(`Token[${i}]: missing or invalid 't' field`);
      continue;
    }

    switch (tok.t) {
      case 'op':
        if (!VALID_OPS.has(String(tok.v))) {
          errors.push(`Token[${i}]: op.v must be one of +, -, *, /`);
        }
        break;
      case 'lparen':
      case 'rparen':
        break;
      case 'num': {
        const n = Number(tok.v);
        if (!Number.isFinite(n)) {
          errors.push(`Token[${i}]: num.v must be a finite number`);
        }
        break;
      }
      case 'ref':
        if (!tok.kind || !REF_KINDS.has(tok.kind)) {
          errors.push(
            `Token[${i}]: ref.kind must be one of ${[...REF_KINDS].join(', ')} ` +
              `(calculated metrics cannot reference dimensions)`
          );
        } else if (typeof tok.key !== 'string' || tok.key.length === 0) {
          errors.push(`Token[${i}]: ref.key must be a non-empty string`);
        } else if (tok.kind === 'metric') {

          if (!ALL_KNOWN_METRICS.has(tok.key) && !knownCalcKeySet.has(tok.key)) {
            errors.push(`Token[${i}]: unknown metric ref '${tok.key}'`);
          }
        } else if (EVENT_REF_KINDS.has(tok.kind)) {
          const bareKey = String(tok.key).trim().toLowerCase();
          if (bareKey !== tok.key) {
            errors.push(`Token[${i}]: event ref key must be a bare lowercase event name`);
          }
          if (!validEventKeys.includes(bareKey)) {
            errors.push(
              `Token[${i}]: event ref '${tok.key}' is not a configured event for this campaign`
            );
          }
        }
        break;
      default:
        errors.push(`Token[${i}]: unknown token type '${tok.t}'`);
    }
  }

  for (let i = 0; i < tokens.length - 1; i++) {
    if (
      tokens[i].t === 'op' &&
      tokens[i].v === '/' &&
      tokens[i + 1].t === 'num' &&
      Number(tokens[i + 1].v) === 0
    ) {
      errors.push('Literal division by zero (/ 0) is not allowed in formulas');
      break;
    }
  }

  type State = 'expect-operand' | 'expect-operator';
  let state: State = 'expect-operand';
  let depth = 0;

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (state === 'expect-operand') {
      if (tok.t === 'lparen') {
        depth++;
        if (depth > 1) {
          errors.push(`Token[${i}]: nested parentheses beyond one level are not allowed`);
        }
      } else if (isOperand(tok)) {
        state = 'expect-operator';
      } else {
        errors.push(`Token[${i}]: expected an operand or '(' but got '${tok.t}'`);
      }
    } else if (tok.t === 'rparen') {
      if (depth === 0) {
        errors.push(`Token[${i}]: unmatched ')'`);
      } else {
        depth--;
      }
    } else if (tok.t === 'op') {
      state = 'expect-operand';
    } else {
      errors.push(`Token[${i}]: expected an operator or ')' but got '${tok.t}'`);
    }
  }

  if (state !== 'expect-operator') {
    errors.push('Formula ends in an incomplete expression (trailing operator or open paren)');
  }
  if (depth !== 0) {
    errors.push('Formula has unclosed parentheses');
  }

  let hasPixelMetric = false;
  let hasAdvertiserMetric = false;
  let hasServed = false;
  let hasMatched = false;
  let hasEvent = false;

  for (const tok of tokens) {
    if (tok.t === 'ref') {
      if (tok.kind === 'event') {
        hasPixelMetric = true;
        hasEvent = true;
      } else if (tok.kind === 'matched_event') {

        hasPixelMetric = true;
        hasEvent = true;
      } else if (tok.kind === 'sum_event' || tok.kind === 'count_event') {

        hasAdvertiserMetric = true;
      } else if (tok.kind === 'metric') {

        if (PIXEL_METRICS.has(tok.key!)) {
          hasPixelMetric = true;
          if (SERVED_METRICS.has(tok.key!)) hasServed = true;
          if (ATTRIBUTION_METRICS.has(tok.key!)) hasMatched = true;
        } else if (ADVERTISER_METRICS.has(tok.key!)) {
          hasAdvertiserMetric = true;
        }
      }
    }
  }

  if (hasPixelMetric && hasAdvertiserMetric) {
    errors.push(
      'Formula mixes pixel-family metrics with advertiser-family metrics. Use one family per formula.'
    );
  }

  if (hasServed && hasMatched) {
    errors.push(
      'served_impression_count and matched_impression_count cannot be combined in one formula ' +
        '(cross-source: they come from separate SQL queries).'
    );
  }

  if (hasServed && hasEvent) {
    errors.push(
      'Served-side metrics (Served Impressions, Revenue, Cost, RPM, CPM) cannot be combined with ' +
        'events in one formula (cross-source: events come from the attribution query). ' +
        'No report type can compute this formula.'
    );
  }

  if (targetMode === 'override') {
    if (!targetColumn || !SLUG_PATTERN.test(targetColumn)) {
      errors.push('override mode requires target_column to be a non-empty slug (^[a-z0-9_]+$)');
    } else if (!ALL_KNOWN_METRICS.has(targetColumn)) {
      errors.push(
        `override target_column '${targetColumn}' is not a known metric (calculated columns can only override metrics, not dimensions)`
      );
    }
  } else if (catalogKeys.has(key)) {
    errors.push(
      `calc metric key '${key}' shadows an existing metric or dimension name — choose a different key`
    );
  } else if (validEventKeys.includes(key)) {
    errors.push(
      `calc metric key '${key}' collides with a configured event key — choose a different key`
    );
  }

  return errors;
}

export function validateCalculatedMetricRow(
  row: CalculatedMetricRowState,
  allRows: CalculatedMetricRowState[],
  validEventKeys: string[],
  overrideTargetKeys: Set<string>,
  catalogKeys: Set<string> = new Set()
): string[] {
  const errors: string[] = [];
  const key = String(row.key || '').trim();

  if (!key || !SLUG_PATTERN.test(key)) {
    errors.push('key must be a non-empty slug (^[a-z0-9_]+$)');
  }

  if (!String(row.display_label || '').trim()) {
    errors.push('display_label is required');
  }

  if (row.target_mode === 'override') {
    if (!row.target_column) {
      errors.push('target_column is required for override mode');
    } else if (!overrideTargetKeys.has(row.target_column)) {
      errors.push(`target_column '${row.target_column}' is not a valid override target`);
    }
  }

  const decimals = row.format?.decimals;
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 6) {
    errors.push('format.decimals must be an integer between 0 and 6');
  }

  const duplicateKey = allRows.filter(
    (other) => other._rowId !== row._rowId && String(other.key || '').trim() === key
  );
  if (key && duplicateKey.length > 0) {
    errors.push(`key '${key}' is duplicated — each calculated metric must have a unique key`);
  }

  const knownCalcKeys = allRows
    .map((other) => String(other.key || '').trim())
    .filter((k) => k.length > 0 && k !== key);

  errors.push(
    ...validateFormulaTokens(
      row.formula,
      validEventKeys,
      row.target_mode,
      key,
      row.target_column || undefined,
      knownCalcKeys,
      catalogKeys
    )
  );

  return errors;
}

export function validateAllCalculatedMetrics(
  rows: CalculatedMetricRowState[],
  validEventKeys: string[],
  overrideTargetKeys: Set<string>,
  catalogKeys: Set<string> = new Set()
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  rows.forEach((row, index) => {
    const rowErrors = validateCalculatedMetricRow(
      row,
      rows,
      validEventKeys,
      overrideTargetKeys,
      catalogKeys
    );
    rowErrors.forEach((message) => {
      errors.push(`Calculated metric ${index + 1}: ${message}`);
    });
  });
  return { valid: errors.length === 0, errors };
}

export function rowHasDraftContent(row: CalculatedMetricRowState): boolean {
  return (
    String(row.key || '').trim().length > 0 ||
    String(row.display_label || '').trim().length > 0 ||
    (row.formula?.tokens?.length ?? 0) > 0 ||
    Boolean(row.target_column)
  );
}
